import React, { useEffect, useRef, useState } from 'react';
import { Logo } from '@cotera/client/v0/components/ui/logo';
import { ShimmerText } from '@cotera/client/v0/components/ui/shimmer-text';

const MESSAGES = [
  'Reticulating splines',
  'Setting controls for the heart of the sun',
  'Reversing the polarity of the neutron flow',
  'Calibrating the flux capacitor',
  'Consulting the oracle',
  'Warming up the dilithium crystals',
  'Compiling the universe',
  'Diverting power to main deflector',
  'Initializing infinite improbability drive',
  'Charging the forward arrays',
  'Routing auxiliary power',
  'Aligning the phase variance buffers',
  'Teaching a neural net to feel love',
  'Tuning the heisenberg compensators',
  'Spinning up the hamster wheels',
  'Negotiating with the cloud',
  'Asking nicely',
  'Folding spacetime',
  'Synchronizing the tachyon emitters',
  'Establishing subspace link',
  'Loading a very small shell script',
  'Preparing the turbo encabulator',
  'Solving P vs NP real quick',
  'Waking up the AI',
  'Calculating the meaning of life',
];

const TYPE_MS = 60;
const DOT_MS = 200;
const ERASE_MS = 20;

function pickRandom(exclude: string): string {
  let next: string;
  do {
    next = MESSAGES[Math.floor(Math.random() * MESSAGES.length)]!;
  } while (next === exclude && MESSAGES.length > 1);
  return next;
}

function useTypewriter(): string {
  const firstMessage = useRef(pickRandom(''));
  const [display, setDisplay] = useState(firstMessage.current);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const current = firstMessage.current;
    let cancelled = false;

    const schedule = (fn: () => void, ms: number) => {
      if (!cancelled) {
        timeoutRef.current = setTimeout(fn, ms);
      }
    };

    const typeIn = (text: string, i: number) => {
      if (cancelled) {
        return;
      }
      if (i <= text.length) {
        setDisplay(text.slice(0, i));
        schedule(() => typeIn(text, i + 1), TYPE_MS);
      } else {
        typeDots(text, 0);
      }
    };

    const typeDots = (text: string, dotCount: number) => {
      if (cancelled) {
        return;
      }
      if (dotCount < 3) {
        setDisplay(text + '.'.repeat(dotCount + 1));
        schedule(() => typeDots(text, dotCount + 1), DOT_MS);
      } else {
        schedule(() => eraseDots(text, 3), DOT_MS);
      }
    };

    const eraseDots = (text: string, dotCount: number) => {
      if (cancelled) {
        return;
      }
      if (dotCount > 0) {
        setDisplay(text + '.'.repeat(dotCount - 1));
        schedule(() => eraseDots(text, dotCount - 1), DOT_MS);
      } else {
        erase(text, text.length);
      }
    };

    const erase = (text: string, i: number) => {
      if (cancelled) {
        return;
      }
      if (i >= 0) {
        setDisplay(text.slice(0, i));
        schedule(() => erase(text, i - 1), ERASE_MS);
      } else {
        const next = pickRandom(text);
        typeIn(next, 0);
      }
    };

    // First message is already displayed; start by playing dots forward
    typeDots(current, 0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return display;
}

/**
 * Full-page branded loading screen with rotating status messages.
 * Used for both the initial app boot and onboarding setup.
 */
export const BootLoading: React.FC<{
  footer?: React.ReactNode;
  message?: React.ReactNode;
}> = ({ footer, message }) => {
  const display = useTypewriter();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-10 p-4">
      <Logo className="w-16 h-16 rounded-xl animate-glow-pulse" />
      {message !== undefined ? (
        <div className="text-center">{message}</div>
      ) : (
        <ShimmerText
          variant="muted"
          className="text-xl font-medium font-mono min-h-[1.75rem]"
        >
          {display}
        </ShimmerText>
      )}
      {footer !== undefined && footer !== null && (
        <div className="absolute bottom-8 text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  );
};
